import React from 'react';
import { Button } from 'components/components/atoms';
import { Container } from 'components/shared';
import styled, { css } from 'styled-components';
import tw from 'twin.macro';
import { getFieldValue } from '@sitecore-jss/sitecore-jss-react';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import breakpoints from 'utils/breakpoints';
import cx from 'classnames';
import trackingEvents from 'utils/trackingEvents';
import { track } from 'utils/tracking';
import useNewColorSchemaOptions from 'utils/hooks/useNewColorSchemaOptions';

const StyledContainer = styled(Container)`
  ${({ reducedPadding }) =>
    reducedPadding &&
    `padding-bottom: 0;
    padding-top: 0;`}
`;

const Heading = styled.h2`
  @media ${breakpoints.lg} {
    font-size: 2.25rem;
  }
  @media ${breakpoints.xl} {
    ${({ download }) =>
      !download ? 'font-size: 2.875rem;' : 'font-size: 2.15rem;'}
  }
`;

const boxShadow = css`
  box-shadow: 0px 10px 40px rgba(0, 0, 0, 0.2);
`;

const StyledDiv = styled.div`
  transition: box-shadow 0.2s ease-in-out;
  ${({ shadowed }) => shadowed && boxShadow}
  ${({ shadowedOnHover }) =>
    shadowedOnHover &&
    `
    border-radius: 0.625rem;
    &:hover {
      border-color: white;
      ${boxShadow}
    }
  `}
  
  ${({ showNewSchemaColor, newSchemaColorHexaCode }) =>
    showNewSchemaColor && `background-color: ${newSchemaColorHexaCode};`}
  ${({ showNewSchemaColor, newSchemaTextColorHexaCode }) =>
    showNewSchemaColor && `color: ${newSchemaTextColorHexaCode};`}
`;

const StyledButton = styled(Button)`
  @media ${breakpoints.md} {
    ${tw`px-4 h-auto`}
  }
  @media ${breakpoints.lg} {
    width: 12.7rem;
    ${tw`px-0`}
  }
  &:hover {
    ${({ showNewSchemaColor, textColor }) =>
      showNewSchemaColor && `background-color: ${textColor};`}
    ${({ showNewSchemaColor, color }) =>
      showNewSchemaColor && `color: ${color};`}
  }
`;

const getButtonColor = (textStyle, filled) => {
  if (textStyle === 'blue-white' || textStyle === 'blue-white-border') {
    return 'action-blue';
  }

  if (textStyle === 'blue-blue') {
    return 'mattamy-blue';
  }

  if (!filled && textStyle === 'white-blue') {
    return 'white';
  }

  if (filled && textStyle === 'white-blue') {
    return 'mattamy-blue';
  }
};

const Wrapper = ({ favoritesBanner, children, reducedPadding }) =>
  !favoritesBanner ? (
    <StyledContainer reducedPadding>{children}</StyledContainer>
  ) : (
    <div className='w-full'>{children}</div>
  );

const TitleCTA = ({
  pdfFile = {},
  text = '',
  ctaText = '',
  textStyleDownload = '',
  fields,
  download = false,
  favoritesBanner = false,
  rendering,
}) => {
  let componentName;

  if (rendering) {
    componentName = rendering.componentName;
  }
  const {
    showNewSchemaColor,
    newSchemaColorHexaCode,
    newSchemaTextColorHexaCode,
  } = useNewColorSchemaOptions();

  const title = getFieldValue(fields, 'title');
  const firstCtaLink = getFieldValue(fields, 'firstCtaLink') || {};
  const secondCtaLink = getFieldValue(fields, 'secondCtaLink') || {};
  const textStyle =
    getFieldValue(fields, 'textStyle') || textStyleDownload || 'blue-white';
  const reducedPadding = getFieldValue(fields, 'reducedPadding') || false;

  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['xs', 'sm'].includes(breakpoint);

  if (download && (!ctaText || !pdfFile.href)) {
    return null;
  }

  if (!download && !firstCtaLink.text && !secondCtaLink.text) {
    return null;
  }

  let trackEventId;
  if (componentName === 'UTourCTA') {
    trackEventId = trackingEvents.BookSelfGuidedTour;
  }

  return (
    <section
      className={cx('flex flex-col items-center justify-start', {
        'w-screen': !favoritesBanner,
        'w-full': favoritesBanner,
        'py-10': !reducedPadding && !download,
        'pt-10': !reducedPadding && download,
      })}
    >
      <Wrapper
        favoritesBanner={favoritesBanner}
        reducedPadding={reducedPadding}
      >
        <StyledDiv
          shadowed={
            !showNewSchemaColor && textStyle === 'blue-white' && !download
          }
          shadowedOnHover={download}
          showNewSchemaColor={showNewSchemaColor}
          newSchemaColorHexaCode={newSchemaColorHexaCode}
          newSchemaTextColorHexaCode={newSchemaTextColorHexaCode}
          className={cx(
            'md:flex-wrap flex flex-col md:flex-row items-center justify-between pl-30-mattamy pr-8',
            {
              'rounded-lg md:pl-20 md:py-4 py-10 lg:py-8': !download,
              'rounded-lg md:pl-10 py-30-mattamy md:py-6': download,
              'border-2 border-action-blue':
                download && textStyle === 'blue-white' && !showNewSchemaColor,
              'border-2 border-action-blue text-mattamy-blue':
                textStyle === 'blue-white-border' && !showNewSchemaColor,
              'text-mattamy-blue bg-white':
                textStyle === 'blue-white' && !showNewSchemaColor,
              'text-mattamy-blue bg-light-blue':
                textStyle === 'blue-blue' && !showNewSchemaColor,
              'text-white bg-mattamy-blue':
                textStyle === 'white-blue' && !showNewSchemaColor,
              'border-2':
                download && textStyle === 'blue-white' && showNewSchemaColor,
            },
          )}
        >
          <Heading
            className={cx(
              'font-bold font-trade-gothic-20 text-center md:text-left md:mt-4 mb-5 md:mb-2 md:mr-8 leading-tight',
              {
                'text-2xl mb-5 md:mb-2': download,
                'text-3xl mb-1 md:mb-2': !download,
              },
            )}
            download={download}
          >
            {!download ? title : text}
          </Heading>
          <div className='w-full md:w-auto flex flex-col md:flex-row items-center justify-center md:justify-end'>
            {((!download && firstCtaLink.text) || (download && ctaText)) && (
              <div
                className={cx('', {
                  'w-full': isMobile,
                  'md:mr-8 lg:mr-10': secondCtaLink.text,
                })}
              >
                {!download && firstCtaLink.text && (
                  <StyledButton
                    className={cx({
                      // "hover:bg-black hover:text-white": showNewSchemaColor,
                      'hover:bg-action-blue hover:text-white':
                        !firstCtaLink.primary &&
                        textStyle === 'blue-white' &&
                        !showNewSchemaColor,
                      'hover:bg-mattamy-blue-dark hover:text-white hover:border-mattamy-blue-dark':
                        !firstCtaLink.primary &&
                        // ["blue-blue", "white-blue"].includes(textStyle) &&
                        ['blue-blue'].includes(textStyle) &&
                        !showNewSchemaColor,
                    })}
                    color={
                      !showNewSchemaColor
                        ? getButtonColor(textStyle)
                        : newSchemaColorHexaCode
                    }
                    textColor={
                      !showNewSchemaColor
                        ? getButtonColor(textStyle, isMobile)
                        : newSchemaTextColorHexaCode
                    }
                    variant={
                      (!showNewSchemaColor &&
                        (firstCtaLink.primary || isMobile)) ||
                      textStyle === 'blue-white-border'
                        ? 'filled'
                        : 'outlined'
                    }
                    showNewSchemaColor={showNewSchemaColor}
                    href={firstCtaLink.href}
                    target={firstCtaLink.target}
                    buttonDescription={title + ' ' + firstCtaLink.text}
                    linkWrapperDescription={title + ' ' + firstCtaLink.text}
                    fullWidthMobile
                    onClick={() => {
                      if (trackEventId) {
                        track({ eventId: trackEventId });
                      }
                      if (componentName === 'UTourCTA') {
                        window.dataLayer.push({ event: 'start-tour' });
                      }
                      if (
                        firstCtaLink.text
                          .toLowerCase()
                          .includes('quick move-ins')
                      ) {
                        window.dataLayer.push({ event: 'view-qmi' });
                      }
                      if (
                        firstCtaLink.text.toLowerCase().includes('floorplans')
                      ) {
                        window.dataLayer.push({ event: 'view-floorplans' });
                      }
                    }}
                  >
                    {firstCtaLink.text}
                  </StyledButton>
                )}
                {download && ctaText && (
                  <StyledButton
                    className={cx({
                      // "hover:bg-black hover:text-white": showNewSchemaColor,
                      'hover:bg-action-blue hover:text-white':
                        !firstCtaLink.primary &&
                        textStyle === 'blue-white' &&
                        !showNewSchemaColor,
                      'hover:bg-mattamy-blue-dark hover:text-white hover:border-mattamy-blue-dark':
                        !firstCtaLink.primary &&
                        // ["blue-blue", "white-blue"].includes(textStyle) &&
                        ['blue-blue'].includes(textStyle) &&
                        !showNewSchemaColor,
                    })}
                    color={
                      !showNewSchemaColor
                        ? getButtonColor(textStyle)
                        : newSchemaColorHexaCode
                    }
                    textColor={
                      !showNewSchemaColor
                        ? getButtonColor(textStyle, true)
                        : newSchemaTextColorHexaCode
                    }
                    variant={!showNewSchemaColor ? 'filled' : 'outlined'}
                    showNewSchemaColor={showNewSchemaColor}
                    href={pdfFile.href}
                    downloadable={true}
                    buttonDescription={text + ' ' + ctaText}
                    linkWrapperDescription={text + ' ' + ctaText}
                    downloadLabel={pdfFile.label}
                    fullWidthMobile
                  >
                    {ctaText}
                  </StyledButton>
                )}
              </div>
            )}
            {!download && secondCtaLink.text && (
              <StyledButton
                className={cx('mt-5 md:mt-0', {
                  // "hover:bg-black hover:text-white": showNewSchemaColor,
                  'hover:bg-action-blue hover:text-white':
                    !secondCtaLink.primary &&
                    textStyle === 'blue-white' &&
                    !showNewSchemaColor,
                  'hover:bg-mattamy-blue-dark hover:text-white hover:border-mattamy-blue-dark':
                    !secondCtaLink.primary &&
                    // ["blue-blue", "white-blue"].includes(textStyle) &&
                    ['blue-blue'].includes(textStyle) &&
                    !showNewSchemaColor,
                })}
                color={
                  !showNewSchemaColor
                    ? getButtonColor(textStyle)
                    : newSchemaColorHexaCode
                }
                textColor={
                  !showNewSchemaColor
                    ? getButtonColor(textStyle, isMobile)
                    : newSchemaTextColorHexaCode
                }
                variant={
                  !showNewSchemaColor && (secondCtaLink.primary || isMobile)
                    ? 'filled'
                    : 'outlined'
                }
                showNewSchemaColor={showNewSchemaColor}
                href={secondCtaLink.href}
                target={secondCtaLink.target}
                linkWrapperDescription={title + ' ' + secondCtaLink.text}
                buttonDescription={title + ' ' + secondCtaLink.text}
                fullWidthMobile
                onClick={() => {
                  if (trackEventId) {
                    track({ eventId: trackEventId });
                  }
                  if (componentName === 'UTourCTA') {
                    window.dataLayer.push({ event: 'start-tour' });
                  }
                  if (
                    secondCtaLink.text.toLowerCase().includes('quick move-ins')
                  ) {
                    window.dataLayer.push({ event: 'view-qmi' });
                  }
                  if (secondCtaLink.text.toLowerCase().includes('floorplans')) {
                    window.dataLayer.push({ event: 'view-floorplans' });
                  }
                }}
              >
                {secondCtaLink.text}
              </StyledButton>
            )}
          </div>
        </StyledDiv>
      </Wrapper>
    </section>
  );
};

export default TitleCTA;
